import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateRole, Permission } from '@mys/contracts';
import { ADMIN_ROLES } from '@mys/contracts/constants';
import { ApiError, api } from './api.js';

/* ============================================================================
   /settings/roles — reading and editing the permission matrix.
   docs/ADMIN-ROADMAP.md 1.4c
   ----------------------------------------------------------------------------
   The screen renders what the SERVER says exists: the roles, the permissions
   and the owner-only list all arrive in one `GET /api/admin/roles`. Nothing
   here reads `PERMISSIONS` or `ROLE_PERMISSIONS` at runtime, and that is the
   point of slice 1.4 — those constants are the seed a fresh database starts
   with, so a dashboard that rendered them would show the matrix as it shipped
   rather than the matrix as it is.

   `ADMIN_ROLES` is the one exception and it is used for ORDER only, never as
   the set of roles. From `@mys/contracts/constants`, never the package index:
   one runtime import from the index drags Zod into this bundle. CLAUDE.md.
   ========================================================================== */

/* --- the shapes this screen reads ------------------------------------------ */

/**
 * The matrix as it ARRIVES, with every permission left as a plain string.
 *
 * Not `RoleDto`/`RoleMatrix` from contracts, and the difference is the point:
 * those type the grants as `Permission`, which is a claim about a response
 * nothing here validates — the dashboard cannot run the Zod schema without
 * dragging Zod into the bundle. A newer API can name a permission this build
 * has never heard of, and `string` is what that actually is. The screen then
 * renders it with the key as its label rather than hiding a live gate until
 * somebody deploys.
 */
export interface RoleView {
  key: string;
  label: string;
  builtIn: boolean;
  permissions: string[];
  members: number;
}

export interface MatrixView {
  roles: RoleView[];
  permissions: string[];
  ownerOnly: string[];
}

/* --- what a permission is called on screen --------------------------------- */

/**
 * The 28 permissions in the owner's words.
 *
 * `Record<Permission, string>` rather than a loose map, so adding a permission
 * to contracts without a label here does not compile. The alternative is a
 * row on this screen reading `undefined`, which is exactly the failure the
 * two content bundles are shape-checked to prevent on the public site.
 *
 * The key is shown beside the label anyway, in small type, because the API's
 * own refusals name it — "Only the owner can hold: invoices:create." has to
 * be findable on the screen that caused it.
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
  'leads:view': 'See the leads board',
  'leads:edit': 'Change a lead and add notes',
  'quotes:view': 'See quotes',
  'quotes:create': 'Make a quote',
  'quotes:send': 'Send a quote',
  'clients:view': 'See clients',
  'clients:create': 'Add a client',
  'clients:edit': 'Change a client, and their domain and hosting records',
  'clients:secrets': 'Store and reveal hosting and registrar logins',
  'projects:view': 'See projects',
  'projects:create': 'Start a project',
  'projects:edit': 'Change a project',
  'invoices:view': 'See invoices',
  'invoices:create': 'Raise an invoice',
  'invoices:settle': 'Record a cash, UPI or bank payment',
  'care-plans:view': 'See care plans',
  'care-plans:create': 'Start a care plan',
  'care-plans:edit': 'Change a care plan',
  'edit-requests:view': 'See change requests',
  'edit-requests:edit': 'Work on a change request',
  'posts:view': 'See the blog',
  'posts:create': 'Write a post',
  'posts:edit': 'Edit a post',
  'posts:delete': 'Delete a post',
  'testimonials:view': 'See testimonials',
  'testimonials:approve': 'Approve a testimonial',
  'analytics:view': 'See analytics',
  'security:view-all': "See everyone's login history",
  'settings:business': 'Change the invoice details',
  'settings:team': 'Invite colleagues, and edit roles',
};

/**
 * A permission's label, falling back to the key.
 *
 * The fallback is not decoration: a newer API than this bundle can return a
 * permission this build has never heard of, and the honest thing to render is
 * the key rather than a blank row with a live checkbox beside it.
 */
export function permissionLabel(permission: string): string {
  return (PERMISSION_LABELS as Record<string, string | undefined>)[permission] ?? permission;
}

/**
 * The module half of a permission, as a heading.
 *
 * Derived rather than mapped, so a new module cannot arrive without a name:
 * `care-plans` reads "Care plans" and `edit-requests` reads "Edit requests"
 * with no table to forget to update.
 */
export function moduleLabel(module: string): string {
  const words = module.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface PermissionGroup {
  module: string;
  label: string;
  permissions: string[];
}

/** The permissions grouped by module, in the order the API sent them. */
export function groupByModule(permissions: readonly string[]): PermissionGroup[] {
  const groups: PermissionGroup[] = [];
  for (const permission of permissions) {
    const module = permission.split(':')[0] ?? permission;
    const last = groups[groups.length - 1];
    if (last && last.module === module) last.permissions.push(permission);
    else groups.push({ module, label: moduleLabel(module), permissions: [permission] });
  }
  return groups;
}

/**
 * The columns, left to right: `owner` first, then the other built-ins in the
 * order they shipped, then the roles the owner created, by label.
 *
 * The API answers `builtIn desc, key asc`, which puts Accounts first and the
 * owner fourth — and the owner column is the one every other column is read
 * against, so it belongs at the left edge. Sorted here rather than in the API
 * because it is a question about this screen, and because Postgres cannot
 * order by a hand-written sequence without being told it twice.
 */
export function sortRoles(roles: readonly RoleView[]): RoleView[] {
  const rank = (role: RoleView): number => {
    const index = (ADMIN_ROLES as readonly string[]).indexOf(role.key);
    return index === -1 ? ADMIN_ROLES.length : index;
  };
  return [...roles].sort(
    (a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label) || a.key.localeCompare(b.key),
  );
}

/* --- the draft ------------------------------------------------------------- */

/** A role key to the permissions it is ticked for, as the screen holds it. */
export type Draft = Record<string, string[]>;

export function draftFrom(matrix: MatrixView): Draft {
  const draft: Draft = {};
  for (const role of matrix.roles) draft[role.key] = [...role.permissions];
  return draft;
}

/**
 * The draft after one tick — **including when there is no draft yet.**
 *
 * That `draft ?? draftFrom(matrix)` is the whole reason this is a function.
 * The screen holds its draft as `Draft | null`, and the version that answered
 * `if (!current) return current` DROPPED a tick that landed while it was null
 * — silently, with the box coming back unticked and "nothing changed" beside
 * it. The window was the render after a save; that particular window is gone
 * now (the save no longer nulls the draft), but the call site should not be
 * the thing standing between a click and the state it changes. Total here,
 * so it cannot be lost there.
 */
export function toggled(
  draft: Draft | null,
  matrix: MatrixView,
  roleKey: string,
  permission: string,
): Draft {
  const base = draft ?? draftFrom(matrix);
  const held = base[roleKey] ?? [];
  return {
    ...base,
    [roleKey]: held.includes(permission)
      ? held.filter((p) => p !== permission)
      : [...held, permission],
  };
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, index) => value === right[index]);
}

/**
 * Which roles the owner has actually changed, in the matrix's own order.
 *
 * Only these are sent. Saving every role would rewrite rows nobody touched —
 * on a screen whose whole contract is "the last save wins, visibly", that
 * would make one person's save overwrite another's edit to a role neither of
 * them opened.
 */
export function changedRoles(matrix: MatrixView, draft: Draft): string[] {
  return matrix.roles
    .filter((role) => {
      const next = draft[role.key];
      return next !== undefined && !sameSet(role.permissions, next);
    })
    .map((role) => role.key);
}

/* --- hooks ----------------------------------------------------------------- */

const KEY = ['roles'];

export function useRoleMatrix() {
  return useQuery({
    queryKey: KEY,
    queryFn: ({ signal }) => api.get<{ ok: true } & MatrixView>('/api/admin/roles', signal),
  });
}

export interface SaveOutcome {
  /** The roles that landed, in the order they were sent. */
  saved: string[];
  /** The first one that did not, and what the API said about it. */
  failed: { key: string; message: string } | null;
}

/**
 * One PUT per changed role, in sequence, stopping at the first refusal.
 *
 * Sequential rather than `Promise.all`, and it stops rather than carrying on,
 * because a partial failure has to be NAMEABLE: "Sales saved, Developer did
 * not, and here is why" is actionable, and "something went wrong" over four
 * parallel requests is not. The screen keeps the draft after a failure, so
 * the refused role can be corrected and sent again — the saved ones stop
 * counting as changed by themselves once the refetch lands.
 */
export function useSaveRoles() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (
      changes: readonly { key: string; permissions: string[] }[],
    ): Promise<SaveOutcome> => {
      const saved: string[] = [];
      for (const change of changes) {
        try {
          await api.put(`/api/admin/roles/${change.key}`, { permissions: change.permissions });
          saved.push(change.key);
        } catch (error) {
          return {
            saved,
            failed: {
              key: change.key,
              message: error instanceof ApiError ? error.message : 'That did not work.',
            },
          };
        }
      }
      return { saved, failed: null };
    },
    /* onSettled, not onSuccess: a run that refused halfway still changed the
       roles before it, and a screen showing the pre-save matrix after that is
       lying about which half landed. */
    onSettled: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCreateRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRole) =>
      api.post<{ ok: true; role: RoleView }>('/api/admin/roles', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRenameRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, label }: { key: string; label: string }) =>
      api.put<{ ok: true; role: RoleView }>(`/api/admin/roles/${key}`, { label }),
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.del<void>(`/api/admin/roles/${key}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}
